import { useCallback, useState } from "react";
import {
  battleSeconds,
  createBattleReducer,
  initialBattle,
  type BattleAction,
  type BattleContext,
  type BattleState,
} from "../game/battle";
import type { Key } from "../game/round";
import { useRoundMachine } from "./useRound";

export interface BattleHandle {
  state: BattleState;
  seconds: number | null;
  now: number;
  press: (key: Key) => void;
  stop: () => void;
  finish: () => void;
  /** Pass on this question: one health, like a wrong answer. */
  pass: () => void;
  /** Use the special move. */
  move: () => void;
  /** Show a hint for this question. */
  hint: () => void;
  /** Press an active item the fighter carries. */
  pressItem: (id: string) => void;
}

/** A battle with real clocks. `ctx` is read once, when the battle mounts. */
export function useBattle(ctx: BattleContext, countdown: boolean): BattleHandle {
  const [reducer] = useState(() => createBattleReducer(ctx));
  const { state, seconds, now, press, stop, finish, pass, dispatch } = useRoundMachine<
    BattleState,
    BattleAction
  >(
    reducer,
    () => initialBattle(ctx, countdown ? undefined : 0),
    (s) => s.round,
    battleSeconds(ctx.fighter, ctx.kit),
  );
  const move = useCallback(() => dispatch({ type: "move", now: Date.now() }), [dispatch]);
  const hint = useCallback(() => dispatch({ type: "hint" }), [dispatch]);
  const pressItem = useCallback(
    (id: string) => dispatch({ type: "use-item", id, now: Date.now() }),
    [dispatch],
  );
  return { state, seconds, now, press, stop, finish, pass, move, hint, pressItem };
}
